package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.ClusterPyramidFetch
import fr.frux.core.network.api.TreeClustersApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.data.local.ClusterPyramidLocalDataSource
import fr.frux.shared.data.local.NoOpClusterPyramidLocalDataSource
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.di.IoDispatcher
import fr.frux.shared.domain.model.ClusterPyramid
import fr.frux.shared.domain.repository.ClusterPyramidRepository
import fr.frux.shared.dto.CLUSTER_PYRAMID_FORMAT_VERSION
import fr.frux.shared.dto.ClusterPyramidDto
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlin.time.Clock
import kotlin.time.Duration

/**
 * Pyramide de regroupements (#622, ADR 0039) : copie locale d'abord, JSON embarque a defaut, puis
 * revalidation par ETag. Le stockage local est un confort : toute erreur de lecture, d'ecriture ou
 * de decodage vaut "pas de copie" et ne fait jamais echouer ni le chargement ni la revalidation.
 * La copie en memoire n'est remplacee que par une pyramide au format connu.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class ClusterPyramidRepositoryImpl(
    private val api: TreeClustersApi,
    @IoDispatcher private val dispatcher: CoroutineDispatcher,
    private val local: ClusterPyramidLocalDataSource = NoOpClusterPyramidLocalDataSource,
    private val nowMillis: () -> Long = { Clock.System.now().toEpochMilliseconds() },
) : ClusterPyramidRepository {
    private val mutex = Mutex()
    private val state = MutableStateFlow<ClusterPyramid?>(null)
    private var loaded = false

    // ETag tel que servi (guillemets compris) et date de derniere verification de la copie courante.
    // Une copie embarquee n'a ni l'un ni l'autre : elle se revalide toujours sans If-None-Match.
    private var etag: String? = null
    private var checkedAtMillis: Long? = null

    override val pyramid: StateFlow<ClusterPyramid?> get() = state

    override suspend fun load(embedded: suspend () -> String?) {
        mutex.withLock {
            // Une revalidation reussie avant load a deja pose une pyramide plus fraiche : ne pas la remplacer.
            if (loaded || state.value != null) return
            withContext(dispatcher) {
                val cached = guardLocal("lecture") { local.read() }
                val fromLocal = cached?.let { decode(it.payload) }
                if (cached != null && fromLocal != null) {
                    state.value = fromLocal.toDomain()
                    etag = cached.etag
                    checkedAtMillis = cached.checkedAtMillis
                    Logger.i(TAG, "📦 Pyramide locale servie (etag : ${cached.etag})")
                } else {
                    val fromEmbedded = readEmbedded(embedded)
                    if (fromEmbedded != null) {
                        state.value = fromEmbedded.toDomain()
                        Logger.i(TAG, "📦 Pyramide embarquee servie")
                    }
                }
            }
            loaded = true
        }
    }

    override suspend fun revalidate(maxAge: Duration): Result<Unit> =
        mutex.withLock {
            withContext(dispatcher) {
                val checkedAt = checkedAtMillis
                val fresh = checkedAt != null && nowMillis() - checkedAt < maxAge.inWholeMilliseconds
                if (maxAge > Duration.ZERO && fresh) return@withContext Result.success(Unit)
                runCatchingCancellable {
                    when (val fetch = api.getClusterPyramid(etag)) {
                        is ClusterPyramidFetch.Fetched -> accept(fetch)
                        ClusterPyramidFetch.NotModified -> confirm()
                    }
                }.onFailure { Logger.e(TAG, "❌ Echec de la revalidation de la pyramide", it) }
                    .mapFailureToAppError()
            }
        }

    private suspend fun accept(fetch: ClusterPyramidFetch.Fetched) {
        val dto = fetch.pyramid
        check(dto.formatVersion == CLUSTER_PYRAMID_FORMAT_VERSION) {
            "Format de pyramide inconnu : ${dto.formatVersion}"
        }
        val now = nowMillis()
        state.value = dto.toDomain()
        etag = fetch.etag
        checkedAtMillis = now
        guardLocal("ecriture") { local.write(fetch.etag, json.encodeToString(dto), now) }
        Logger.i(TAG, "🌲 Pyramide mise a jour (etag : ${fetch.etag})")
    }

    private suspend fun confirm() {
        val now = nowMillis()
        checkedAtMillis = now
        guardLocal("confirmation") { local.touch(now) }
        Logger.d(TAG, "📦 Pyramide inchangee (304)")
    }

    private suspend fun readEmbedded(embedded: suspend () -> String?): ClusterPyramidDto? =
        runCatchingCancellable { embedded()?.let { decode(it) } }
            .onFailure { Logger.e(TAG, "❌ Pyramide embarquee illisible", it) }
            .getOrNull()

    private fun decode(payload: String): ClusterPyramidDto? =
        runCatchingCancellable { json.decodeFromString<ClusterPyramidDto>(payload) }
            .onFailure { Logger.e(TAG, "❌ Pyramide illisible, ignoree", it) }
            .getOrNull()
            ?.takeIf { it.formatVersion == CLUSTER_PYRAMID_FORMAT_VERSION }

    private suspend fun <T> guardLocal(
        action: String,
        block: suspend () -> T,
    ): T? =
        runCatchingCancellable { block() }
            .onFailure { Logger.e(TAG, "❌ Echec ($action) de la copie locale de la pyramide", it) }
            .getOrNull()

    private companion object {
        const val TAG = "ClusterPyramidRepository"

        // Meme reglage que le client reseau (FruxHttpClient) : un champ inconnu ou une valeur d'enum
        // future ne doit pas rendre la copie locale illisible.
        val json =
            Json {
                ignoreUnknownKeys = true
                coerceInputValues = true
            }
    }
}
